import { useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import { Download, Filter } from "lucide-react";
import { useLocation, useParams } from "react-router-dom";

import { API_ASSET_EXPORT, API_ASSET_FILTER_TOTAL, API_ASSET_TYPES } from "../../../../config/Endpoints.js";
import { availabilityOptions, superOwnerOptions } from "../../../../config/filters.js";
import { getApi } from "../../../../utils/api.js";
import { capitalize } from "../../../../utils/helpers.js";
import { Card } from "../../../components/shared/card/card.tsx";
import { EmptyStateMessage } from "../../../components/shared/empty-state-message/empty-state-message.tsx";
import PageShell from "../../../components/shared/page-shell/page-shell.tsx";
import { Button, ResponsiveDataTable, Select, Spinner, type ResponsiveColumn } from "../../../components/ui/index.ts";
import { useBreadcrumbs } from "../../../context/breadcrumb/breadcrumb-context.tsx";
import { showError } from "../../../utils/toast.ts";

import styles from "./asset-type-details-page.module.css";

type AssetRecord = {
  manufacture?: string | null;
  model?: string | null;
  processor?: string | null;
  ram?: string | null;
  ssd?: string | null;
  serialNumber?: string | null;
  owners?: { ownerType?: string | null } | null;
  owner?: string | null;
  status?: string | null;
  availability?: string | null;
  assestType?: { name?: string | null; ram?: unknown } | null;
};

type AssetRow = Record<string, unknown> & {
  manufacturer: string;
  model: string;
  processor: string;
  ram: string;
  ssd: string;
  serialNo: string;
  superOwner: string;
  owner: string;
  status: string;
  availability: string;
};

type AssetListResponse = { data?: AssetRecord[]; totalItems?: number };
type Filters = { superOwner: string; owner: string; availability: string };
type SelectOption = { value: string; label: string };
type PageProps = { title?: string };

const PAGE_SIZES = [5, 10, 20, 30, 40] as const;
const DEFAULT_FILTERS: Filters = {
  superOwner: superOwnerOptions[0].value,
  owner: "ALL",
  availability: availabilityOptions[0].value,
};
const CAELIUS_OWNERS: SelectOption[] = [
  { value: "ALL", label: "All" },
  { value: "Caelius", label: "Caelius" },
  { value: "Consultant", label: "Consultant" },
  { value: "SQE", label: "SQE Labs" },
];
const CLIENT_OWNERS: SelectOption[] = [
  { value: "ALL", label: "All" },
  { value: "Salesforce", label: "Salesforce" },
  { value: "Doordash", label: "Doordash" },
];

function displayValue(value: string | null | undefined): string {
  return value?.trim() || "-";
}

function buildAssetParams(assetTypeId: string, filters: Filters, page?: number, size?: number): URLSearchParams {
  const params = new URLSearchParams({ owner: filters.owner, assestTypeId: assetTypeId });
  if (page !== undefined) params.set("page", String(page - 1));
  if (size !== undefined) params.set("size", String(size));
  if (filters.availability !== DEFAULT_FILTERS.availability) params.set("availability", filters.availability);
  if (filters.superOwner !== DEFAULT_FILTERS.superOwner) params.set("superOwner", filters.superOwner);
  return params;
}

function toTableRow(asset: AssetRecord): AssetRow {
  return {
    manufacturer: displayValue(asset.manufacture),
    model: displayValue(asset.model),
    processor: displayValue(asset.processor),
    ram: displayValue(asset.ram),
    ssd: displayValue(asset.ssd),
    serialNo: displayValue(asset.serialNumber),
    superOwner: displayValue(asset.owners?.ownerType),
    owner: displayValue(asset.owner),
    status: displayValue(capitalize(asset.status)),
    availability: displayValue(capitalize(asset.availability)),
  };
}

export default function AssetTypeDetailsPage({ title }: PageProps) {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { setBreadcrumbs } = useBreadcrumbs();
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [allOwners, setAllOwners] = useState<SelectOption[]>([]);
  const [assets, setAssets] = useState<AssetRecord[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  const ownerOptions = useMemo(() => {
    if (filters.superOwner === "CAELIUS_OWNED") return CAELIUS_OWNERS;
    if (filters.superOwner === "CLIENT_OWNED") return CLIENT_OWNERS;
    return [{ value: "ALL", label: "All" }, ...allOwners];
  }, [filters.superOwner, allOwners]);

  const assetTypeName = (location.state as { assetType?: string } | null)?.assetType
    || assets[0]?.assestType?.name || "Asset Details";

  useEffect(() => {
    setBreadcrumbs([
      { label: title || "Asset Overview", path: "/dashboard/assetOverview" },
      { label: assetTypeName },
    ]);
    return () => setBreadcrumbs([]);
  }, [title, assetTypeName, setBreadcrumbs]);

  useEffect(() => {
    let active = true;
    getApi(API_ASSET_TYPES, Cookies.get("accessToken"))
      .then((response: { data: { data?: { ownerTypes?: string[] } } }) => {
        if (active) setAllOwners((response.data?.data?.ownerTypes || []).map((owner) => ({ value: owner, label: owner })));
      })
      .catch((error: unknown) => {
        console.error("Failed to load asset owners:", error);
        if (active) showError("An unexpected error occurred");
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!id) return;
    let active = true;
    setIsLoading(true);
    const params = buildAssetParams(id, appliedFilters, page, pageSize);
    getApi(`${API_ASSET_FILTER_TOTAL}?${params.toString()}`, Cookies.get("accessToken"))
      .then((response: { data: AssetListResponse }) => {
        if (!active) return;
        setAssets(Array.isArray(response.data?.data) ? response.data.data : []);
        setTotalItems(Number(response.data?.totalItems) || 0);
      })
      .catch((error: unknown) => {
        console.error("Failed to load assets:", error);
        if (!active) return;
        setAssets([]);
        setTotalItems(0);
        showError("An unexpected error occurred");
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => { active = false; };
  }, [id, appliedFilters, page, pageSize]);

  const isLaptop = Boolean(assets[0]?.assestType?.ram);
  const rows = useMemo(() => assets.map(toTableRow), [assets]);
  const columns = useMemo<ResponsiveColumn<AssetRow>[]>(() => [
    { id: "manufacturer", header: "Manufacturer", accessor: "manufacturer", sortable: true },
    { id: "model", header: "Model", accessor: "model", sortable: true },
    ...(isLaptop ? [
      { id: "processor", header: "Processor", accessor: "processor", sortable: true },
      { id: "ram", header: "RAM", accessor: "ram", sortable: true },
      { id: "ssd", header: "SSD", accessor: "ssd", sortable: true },
    ] as ResponsiveColumn<AssetRow>[] : []),
    { id: "serialNo", header: "Serial Number", accessor: "serialNo", sortable: true, nowrap: true },
    { id: "superOwner", header: "Super Owner", accessor: "superOwner", sortable: true },
    { id: "owner", header: "Owner", accessor: "owner", sortable: true },
    { id: "status", header: "Status", accessor: "status", sortable: true },
    { id: "availability", header: "Availability", accessor: "availability", sortable: true },
  ], [isLaptop]);

  const handleExport = async () => {
    if (!id) return;
    setIsExporting(true);
    try {
      const params = buildAssetParams(id, filters);
      const response = await fetch(`${API_ASSET_EXPORT}?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${Cookies.get("accessToken")}`,
          "Content-Type": "application/vnd.ms-excel",
        },
      });
      if (!response.ok) {
        showError("No Record Available!");
        return;
      }
      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.href = url;
      link.download = "asset_list.xlsx";
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading asset list:", error);
      showError("Unable to export assets. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  return <PageShell className={styles.page}>
    <Card className={styles.toolbar}>
      <div className={styles.filters}>
        <Select id="superOwnerFilter" label="Super Owner" value={filters.superOwner} options={superOwnerOptions}
          onChange={(superOwner) => setFilters((current) => ({ ...current, superOwner, owner: "ALL" }))} />
        <Select id="ownerFilter" label="Owner" value={filters.owner} options={ownerOptions}
          onChange={(owner) => setFilters((current) => ({ ...current, owner }))} />
        <Select id="availabilityFilter" label="Availability" value={filters.availability} options={availabilityOptions}
          onChange={(availability) => setFilters((current) => ({ ...current, availability }))} />
      </div>
      <div className={styles.actions}>
        <Button startIcon={<Filter size={17} />} onClick={() => { setPage(1); setAppliedFilters({ ...filters }); }}>
          Filter
        </Button>
        <Button variant="muted-outline" startIcon={<Download size={17} />} disabled={isExporting}
          onClick={() => void handleExport()}>{isExporting ? "Exporting..." : "Export"}</Button>
      </div>
    </Card>

    <Card padding="none" className={styles.tableCard}>
      {isLoading ? <div className={styles.loading}><Spinner label="Loading assets" /></div> :
        <ResponsiveDataTable<AssetRow>
          aria-label={`${assetTypeName} assets`}
          columns={columns}
          data={rows}
          serialNumber
          page={page}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={setPage}
          onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          pageSizeOptions={PAGE_SIZES}
          totalLabel={<>Total Assets: <strong>{totalItems}</strong></>}
          emptyState={<EmptyStateMessage message="No records available" />}
        />}
    </Card>
  </PageShell>;
}
